import React, { useState, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATE_STYLES = {
  VALID:   { bg: "#0E9F8E", label: "VERIFIED PRESCRIPTION" },
  REVOKED: { bg: "#D97706", label: "PRESCRIPTION REVOKED" },
  UNKNOWN: { bg: "#C82727", label: "NOT GENUINE" },
};

export default function RxVerifyPage() {
  const [state, setState] = useState("idle"); // idle | uploading | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const verify = useCallback(async (file) => {
    setState("uploading");
    setError(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/api/rx/verify`, { method: "POST", body: fd });
      const j = await r.json();
      setResult(j);
      setState("done");
    } catch (e) {
      setError(String(e));
      setState("error");
    }
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) verify(f);
  };

  const style = result ? STATE_STYLES[result.status] || STATE_STYLES.UNKNOWN : null;

  return (
    <div style={{ minHeight: "100vh", background: "#F5F9FD", padding: "60px 20px", fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ maxWidth: 560, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#123B91", letterSpacing: ".14em", marginBottom: 8 }}>
            DOCSITE · VERIFIED Rx
          </div>
          <h1 style={{ fontSize: 30, color: "#123B91", margin: "8px 0", fontWeight: 800 }}>
            Verify a Prescription
          </h1>
          <p style={{ color: "#3A4658", fontSize: 15, lineHeight: 1.5 }}>
            Drop a DocSite-issued Rx PDF below. We'll check its cryptographic fingerprint
            against our registry and tell you if it's genuine — instantly.
          </p>
        </div>

        <div
          data-testid="rx-verify-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          style={{
            background: "#fff",
            border: `2px dashed ${dragOver ? "#123B91" : "#B8CBE6"}`,
            borderRadius: 14,
            padding: "48px 24px",
            textAlign: "center",
            transition: "border-color .15s",
            cursor: "pointer",
          }}
          onClick={() => document.getElementById("rx-verify-input").click()}
        >
          <input
            id="rx-verify-input"
            data-testid="rx-verify-file-input"
            type="file"
            accept="application/pdf"
            style={{ display: "none" }}
            onChange={(e) => e.target.files?.[0] && verify(e.target.files[0])}
          />
          <div style={{ fontSize: 42, marginBottom: 12, color: "#123B91" }}>⬆</div>
          <div style={{ fontWeight: 700, color: "#123B91", fontSize: 16 }}>
            {state === "uploading" ? "Verifying…" : "Drop a Rx PDF here or click to browse"}
          </div>
          <div style={{ fontSize: 13, color: "#6B7A8F", marginTop: 8 }}>
            Nothing is stored. Only the file's SHA-256 hash is compared.
          </div>
        </div>

        {error && (
          <div data-testid="rx-verify-error" style={{ background: "#FEE", color: "#C82727", padding: 16, borderRadius: 10, marginTop: 16, textAlign: "center", fontWeight: 600 }}>
            {error}
          </div>
        )}

        {result && style && (
          <div data-testid="rx-verify-result" data-state={result.status} style={{ background: "#fff", borderRadius: 14, marginTop: 20, overflow: "hidden", boxShadow: "0 8px 32px rgba(18,59,145,.12)" }}>
            <div style={{ background: style.bg, color: "#fff", padding: "18px 20px", textAlign: "center", fontWeight: 800, letterSpacing: ".06em", fontSize: 14 }}>
              {style.label}
            </div>
            <div style={{ padding: "24px", textAlign: "center" }}>
              {result.rxId && (
                <div style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", color: "#123B91", fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
                  {result.rxId}
                </div>
              )}
              <p style={{ margin: "10px 0", color: "#3A4658", fontSize: 14, lineHeight: 1.5 }}>{result.statusLine1}</p>
              {result.statusLine2 && (
                <p style={{ margin: "6px 0", color: "#20242C", fontSize: 13, fontWeight: 600 }}>{result.statusLine2}</p>
              )}
              {result.pdfHash && (
                <div style={{ marginTop: 20, padding: 12, background: "#F5F9FD", borderRadius: 8, fontSize: 11, color: "#555F70", fontFamily: "ui-monospace, monospace", wordBreak: "break-all" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>PDF Fingerprint (SHA-256)</div>
                  {result.pdfHash}
                </div>
              )}
              <button
                onClick={() => { setResult(null); setState("idle"); }}
                data-testid="rx-verify-another"
                style={{ marginTop: 20, padding: "10px 20px", background: "#123B91", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}
              >
                Verify another
              </button>
            </div>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: 32, fontSize: 12, color: "#6B7A8F" }}>
          Cryptographically verified via SHA-256 hash registry. Powered by DocSite.
        </div>
      </div>
    </div>
  );
}
